import React, { useState } from 'react';
import { X, Phone, Mail, MessageSquare } from 'lucide-react';
import { MessageModal } from './MessageModal';
interface ContactSellerModalProps {
  isOpen: boolean;
  onClose: () => void;
  sellerName: string;
  listingTitle: string;
}
export function ContactSellerModal({
  isOpen,
  onClose,
  sellerName,
  listingTitle
}: ContactSellerModalProps) {
  const [message, setMessage] = useState('');
  const [contactMethod, setContactMethod] = useState<'message' | 'phone' | 'email'>('message');
  const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState({
    title: '',
    message: '',
    type: 'info' as 'success' | 'error' | 'info'
  });
  if (!isOpen) return null;
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (contactMethod === 'message' && !message.trim()) {
      setModalContent({
        title: 'Message Required',
        message: 'Please enter a message to send to the seller.',
        type: 'error'
      });
      setIsMessageModalOpen(true);
      return;
    }
    setModalContent({
      title: 'Message Sent',
      message: `Your inquiry has been sent to ${sellerName}. They will respond within 24 hours.`,
      type: 'success'
    });
    setIsMessageModalOpen(true);
    setTimeout(() => {
      onClose();
      setMessage('');
    }, 2000);
  };
  const handleRevealPhone = () => {
    setModalContent({
      title: 'Phone Number',
      message: `${sellerName}'s phone: +91 98765 43210\n\nNote: This contact has been logged for security purposes.`,
      type: 'info'
    });
    setIsMessageModalOpen(true);
  };
  return <>
      <div data-testid="contact-seller-modal-overlay" className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-id="element-526">
        <div data-testid="contact-seller-modal" className="bg-white border border-gray-300 max-w-lg w-full shadow-lg" data-id="element-527">
          <div className="flex justify-between items-center p-4 border-b border-gray-200" data-id="element-528">
            <h3 data-testid="contact-seller-modal-title" className="font-bold text-lg" data-id="element-529">
              Contact Seller
            </h3>
            <button data-testid="contact-seller-modal-close-btn" onClick={onClose} className="text-gray-400 hover:text-gray-600" data-id="element-530">
              <X className="w-5 h-5" data-id="element-531" />
            </button>
          </div>

          <div className="p-6" data-id="element-532">
            <div data-testid="contact-seller-listing-info" className="mb-4 p-3 bg-gray-50 border border-gray-200" data-id="element-533">
              <p className="text-sm text-gray-600" data-id="element-534">Inquiring about:</p>
              <p data-testid="contact-seller-listing-title" className="font-bold" data-id="element-535">
                {listingTitle}
              </p>
              <p data-testid="contact-seller-seller-name" className="text-sm text-gray-500 mt-1" data-id="element-536">
                Seller: {sellerName}
              </p>
            </div>

            <form data-testid="contact-seller-form" onSubmit={handleSubmit} className="space-y-4" data-id="element-537">
              {/* Contact Method Selection */}
              <div data-id="element-538">
                <label className="block text-sm font-bold text-gray-700 mb-2" data-id="element-539">
                  Contact Method
                </label>
                <div className="grid grid-cols-3 gap-2" data-id="element-540">
                  <button data-testid="contact-method-message-btn" type="button" onClick={() => setContactMethod('message')} className={`p-3 border text-sm font-medium flex flex-col items-center gap-1 ${contactMethod === 'message' ? 'border-[#1f2933] bg-gray-50' : 'border-gray-300 hover:bg-gray-50'}`} data-id="element-541">
                    <MessageSquare className="w-5 h-5" data-id="element-542" />
                    Message
                  </button>
                  <button data-testid="contact-method-phone-btn" type="button" onClick={() => setContactMethod('phone')} className={`p-3 border text-sm font-medium flex flex-col items-center gap-1 ${contactMethod === 'phone' ? 'border-[#1f2933] bg-gray-50' : 'border-gray-300 hover:bg-gray-50'}`} data-id="element-543">
                    <Phone className="w-5 h-5" data-id="element-544" />
                    Phone
                  </button>
                  <button data-testid="contact-method-email-btn" type="button" onClick={() => setContactMethod('email')} className={`p-3 border text-sm font-medium flex flex-col items-center gap-1 ${contactMethod === 'email' ? 'border-[#1f2933] bg-gray-50' : 'border-gray-300 hover:bg-gray-50'}`} data-id="element-545">
                    <Mail className="w-5 h-5" data-id="element-546" />
                    Email
                  </button>
                </div>
              </div>

              {/* Message Input */}
              {contactMethod === 'message' && <div data-id="element-547">
                  <label className="block text-sm font-bold text-gray-700 mb-1" data-id="element-548">
                    Your Message
                  </label>
                  <textarea data-testid="contact-seller-message-input" value={message} onChange={e => setMessage(e.target.value)} placeholder="I'm interested in this property. When can I schedule a site visit?" rows={4} className="w-full p-2 border border-gray-300 bg-gray-50 focus:outline-none focus:border-gray-500 text-sm" data-id="element-549" />
                  <p className="text-xs text-gray-500 mt-1" data-id="element-550">
                    Your contact details will be shared with the seller
                  </p>
                </div>}

              {/* Phone Reveal */}
              {contactMethod === 'phone' && <div className="p-4 bg-blue-50 border border-blue-200" data-id="element-551">
                  <p className="text-sm text-gray-700 mb-3" data-id="element-552">
                    Click below to reveal the seller's phone number
                  </p>
                  <button data-testid="reveal-phone-btn" type="button" onClick={handleRevealPhone} className="w-full py-2 bg-blue-600 text-white text-sm font-bold hover:bg-blue-700" data-id="element-553">
                    Reveal Phone Number
                  </button>
                </div>}

              {/* Email Option */}
              {contactMethod === 'email' && <div data-id="element-554">
                  <label className="block text-sm font-bold text-gray-700 mb-1" data-id="element-555">
                    Your Message
                  </label>
                  <textarea data-testid="contact-seller-email-input" value={message} onChange={e => setMessage(e.target.value)} placeholder="Enter your message..." rows={4} className="w-full p-2 border border-gray-300 bg-gray-50 focus:outline-none focus:border-gray-500 text-sm" data-id="element-556" />
                  <p className="text-xs text-gray-500 mt-1" data-id="element-557">
                    Email will be sent to seller's registered email address
                  </p>
                </div>}

              <div className="flex justify-end gap-3 pt-4" data-id="element-558">
                <button data-testid="contact-seller-cancel-btn" type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 text-sm font-bold hover:bg-gray-50" data-id="element-559">
                  Cancel
                </button>
                <button data-testid="contact-seller-submit-btn" type="submit" className="px-4 py-2 bg-[#1f2933] text-white text-sm font-bold hover:bg-gray-800" data-id="element-560">
                  {contactMethod === 'phone' ? 'Close' : 'Send Inquiry'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <MessageModal isOpen={isMessageModalOpen} onClose={() => setIsMessageModalOpen(false)} title={modalContent.title} message={modalContent.message} type={modalContent.type} data-id="element-561" />
    </>;
}